import * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 whitespace-nowrap',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80',
        primary: 'border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80',
        // Refactored to use theme colors instead of hardcoded Tailwind classes (Phase 6 migration)
        success: 'border-transparent bg-success text-primary-foreground shadow hover:bg-success/80',
        warning: 'border-transparent bg-warning text-primary-foreground shadow hover:bg-warning/80',
        info: 'border-transparent bg-info text-primary-foreground shadow hover:bg-info/80',
        outline: 'text-foreground',
        pending:
          'bg-[hsl(var(--warning)_/_0.2)] text-[hsl(var(--warning))] border-[hsl(var(--warning)_/_0.4)]',
        ongoing:
          'bg-[hsl(var(--info)_/_0.2)] text-[hsl(var(--info)_/_0.8)] border-[hsl(var(--info)_/_0.4)]',
        confirmed:
          'bg-[hsl(var(--success)_/_0.2)] text-[hsl(var(--success))] border-[hsl(var(--success)_/_0.4)]',
        declined:
          'bg-[hsl(var(--destructive)_/_0.2)] text-[hsl(var(--destructive))] border-[hsl(var(--destructive)_/_0.4)]',
        // Lead Request Status variants
        new: 'bg-[hsl(var(--info)_/_0.2)] text-[hsl(var(--info))] border-[hsl(var(--info)_/_0.4)]',
        requested:
          'bg-[hsl(var(--warning)_/_0.2)] text-[hsl(var(--warning))] border-[hsl(var(--warning)_/_0.4)]',
        accepted:
          'bg-[hsl(var(--success)_/_0.2)] text-[hsl(var(--success))] border-[hsl(var(--success)_/_0.4)]',
        rejected:
          'bg-[hsl(var(--destructive)_/_0.2)] text-[hsl(var(--destructive))] border-[hsl(var(--destructive)_/_0.4)]',
        canceled:
          'bg-[hsl(var(--muted)_/_0.4)] text-muted-foreground border-[hsl(var(--muted-foreground)_/_0.4)]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
